import { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';

export const RTCWikiAIFullPage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const chatElRef = useRef<HTMLElement | null>(null);
  const initedRef = useRef(false);

  useEffect(() => {
    if (initedRef.current) return; // guard React StrictMode double-run
    initedRef.current = true;

    // Save & set body margin for a true full-page feel (optional)
    const prevMargin = document.body.style.margin;
    document.body.style.margin = '0';

    // Create and mount the flowise element INSIDE this component
    const chatEl = document.createElement('flowise-fullchatbot');
    chatEl.style.cssText = 'display:block;width:100%;height:100vh;';
    chatElRef.current = chatEl;
    containerRef.current?.appendChild(chatEl);

    // Inject the embed script as a real module (scoped)
    const script = document.createElement('script');
    script.type = 'module';
    script.textContent = `
      import Chatbot from 'https://cdn.jsdelivr.net/npm/flowise-embed@3.0.3/dist/web.js';
      // Initialize once per mount
      if (!window.__rtcFlowiseInited) {
        window.__rtcFlowiseInited = true;
        Chatbot.initFull({
          chatflowid: 'afc84025-f2c9-4f96-9ec6-2ab09197ada0',
          apiHost: '/api/rag/flowise',
          // Forward conservative gen settings + always return source docs (if your flow supports it)
          chatflowConfig: {
            override: { temperature: 0.2, top_p: 0.9, maxTokens: 1024 },
            returnSourceDocuments: true
          },
          theme: {
            chatWindow: {
              showTitle: true,
              showAgentMessages: true,
              title: 'RTC Wiki AI',
              titleAvatarSrc: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="80" font-size="80">🤖</text></svg>',
              titleBackgroundColor: '#2B6CB0',
              titleTextColor: '#ffffff',
              welcomeMessage: "Hello! I'm RTC Wiki AI, your intelligent assistant. How can I help you today?",
              errorMessage: "Sorry, I'm having trouble connecting. Please try again later.",
              backgroundColor: '#ffffff',
              starterPrompts: [
                "How do I do an eJustice offline search?",
                "How do I make someone a Zello account?",
                "How do I view Bedford school cameras?",
                "What setup is at Bryant Pond Rd LPR site?",
                "How do I send a Mobotix video on Slack?"
              ],
              starterPromptFontSize: 13,
              clearChatOnReload: true,
              sourceDocsTitle: 'Sources:',
              renderHTML: true,
              botMessage: {
                backgroundColor: '#F7FAFC',
                textColor: '#2D3748',
                showAvatar: true,
                avatarSrc: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="80" font-size="80">🤖</text></svg>'
              },
              userMessage: {
                backgroundColor: '#2B6CB0',
                textColor: '#ffffff',
                showAvatar: true,
                avatarSrc: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="80" font-size="80">👮</text></svg>'
              },
              textInput: {
                placeholder: 'Ask me anything about RTC...',
                backgroundColor: '#ffffff',
                textColor: '#2D3748',
                sendButtonColor: '#2B6CB0',
                maxChars: 500,
                maxCharsWarningMessage: 'Message too long. Please keep it under 500 characters.',
                autoFocus: true,
                sendMessageSound: true,
                receiveMessageSound: true
              },
              feedback: { color: '#2D3748' },
              dateTimeToggle: { date: true, time: true },
              footer: {
                textColor: '#718096',
                text: 'Powered by',
                company: 'RTC Wiki AI',
                companyLink: '#'
              }
            }
          }
        });
      }
    `;
    scriptRef.current = script;
    document.head.appendChild(script);

    return () => {
      // Teardown: remove the widget we added
      try { chatElRef.current?.remove(); } catch {}
      // Remove the script we injected
      try { scriptRef.current && document.head.removeChild(scriptRef.current); } catch {}
      // Clear any globals used to gate init so future mounts can re-init cleanly
      try { (window as any).__rtcFlowiseInited = false; } catch {}
      // Belt & suspenders: remove any stray Flowise elements the library may have created
      document.querySelectorAll('flowise-fullchatbot, flowise-chatbot').forEach(n => {
        if (n && n.parentElement) n.parentElement.removeChild(n);
      });
      // Restore body margin
      document.body.style.margin = prevMargin;
      // Allow future effect runs (e.g., route back)
      initedRef.current = false;
    };
  }, []);

  // Wrapper ensures it lives only within this page and unmounts on navigation
  return <Box ref={containerRef} w="100%" h="100vh" id="rtc-wiki-ai-container" />;
};
